import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  FileArrowUp, Trash, ChatText, FileText, Download, Wrench, PencilSimple,
  Robot, User as UserIcon, MicrophoneStage, Stop, PaperPlaneTilt, CheckCircle,
} from "@phosphor-icons/react";
import KbEditDialog from "@/components/KbEditDialog";

/**
 * Admin — Chatbot Operator Settings (embedded nell'hub).
 * Tab 1: KB Documenti/Istruzioni (Vademecum Catasto, procedure, manuali)
 * Tab 2: Libreria Template (Word/Excel/PDF scaricabili dagli operatori)
 */
export default function ChatbotOperatorSettings({ embedded = false }) {
  const [tab, setTab] = useState("kb");
  const [kb, setKb] = useState([]);
  const [templates, setTemplates] = useState([]);

  const loadKb = () => api.get("/admin/chatbot-operator/knowledge").then(r => setKb(r.data || []));
  const loadTemplates = () => api.get("/admin/chatbot-operator/templates").then(r => setTemplates(r.data || []));

  useEffect(() => { loadKb(); loadTemplates(); }, []);

  const delKb = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`/admin/chatbot-operator/knowledge/${id}`);
    toast.success("Rimosso"); loadKb();
  };
  const delTpl = async (id) => {
    if (!window.confirm("Eliminare template?")) return;
    await api.delete(`/admin/chatbot-operator/template/${id}`);
    toast.success("Rimosso"); loadTemplates();
  };

  return (
    <div>
      {!embedded && (
        <div className="mb-6">
          <div className="label-eyebrow">Amministrazione</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Chatbot Operator</h1>
        </div>
      )}
      <div className="rounded-xl border border-purple-200 bg-purple-50 p-4 mb-6 flex items-start gap-3">
        <Wrench size={20} className="text-purple-700 shrink-0 mt-0.5" weight="fill" />
        <div className="text-sm text-purple-900">
          <div className="font-semibold">Assistente alla produzione tecnica</div>
          <div className="text-xs mt-0.5">
            Il Chatbot Operator supporta gli operatori interni con Vademecum Catasto, procedure edilizie e libreria template.
            Basato su Claude Sonnet + retrieval BM25 (stessa architettura dell'Agente AI, KB separata).
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="op-chatbot-tabs">
          <TabsTrigger value="kb" data-testid="op-tab-kb">Knowledge Base ({kb.length})</TabsTrigger>
          <TabsTrigger value="instructions" data-testid="op-tab-instructions">Istruzioni</TabsTrigger>
          <TabsTrigger value="templates" data-testid="op-tab-templates">Libreria Template ({templates.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="kb" className="mt-6">
          <OpKbTab kb={kb} onChange={loadKb} onDelete={delKb} />
        </TabsContent>

        <TabsContent value="instructions" className="mt-6">
          <OpTrainingChatTab onKbChange={loadKb} />
        </TabsContent>

        <TabsContent value="templates" className="mt-6">
          <OpTemplatesTab templates={templates} onChange={loadTemplates} onDelete={delTpl} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OpKbTab({ kb, onChange, onDelete }) {
  const [editing, setEditing] = React.useState(null);
  const [mode, setMode] = useState("document"); // document | instruction
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState("");
  const [priority, setPriority] = useState(0);
  const [uploading, setUploading] = useState(false);

  const submit = async () => {
    setUploading(true);
    try {
      if (mode === "document") {
        if (!file) { toast.error("Seleziona un file"); return; }
        const form = new FormData();
        form.append("file", file);
        if (title) form.append("title", title);
        if (tags) form.append("tags", tags);
        form.append("priority", String(priority));
        await api.post("/admin/chatbot-operator/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        if (!content.trim()) { toast.error("Contenuto vuoto"); return; }
        await api.post("/admin/chatbot-operator/instruction", {
          title: title || content.substring(0, 60),
          content, priority,
          tags: tags.split(",").map(t => t.trim()).filter(Boolean),
        });
      }
      toast.success("Salvato");
      setFile(null); setTitle(""); setContent(""); setTags(""); setPriority(0);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="kpi-card">
        <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
          <FileArrowUp size={20} className="text-purple-700" weight="fill" /> Aggiungi alla KB Operator
        </h3>
        <div className="flex gap-2 mb-3">
          <Button type="button" variant={mode === "document" ? "default" : "outline"} className={mode === "document" ? "ht-gradient border-0" : ""} onClick={() => setMode("document")} data-testid="op-mode-doc">Documento (PDF/TXT)</Button>
          <Button type="button" variant={mode === "instruction" ? "default" : "outline"} className={mode === "instruction" ? "ht-gradient border-0" : ""} onClick={() => setMode("instruction")} data-testid="op-mode-inst">Istruzione testuale</Button>
        </div>
        {mode === "document" ? (
          <input type="file" accept=".pdf,.txt,.md" onChange={e => setFile(e.target.files?.[0] || null)} className="block w-full text-sm border border-slate-200 rounded-lg p-2" data-testid="op-kb-file" />
        ) : (
          <Textarea value={content} onChange={e => setContent(e.target.value)} placeholder="Es. Procedura Docfa: verificare planimetria 1:200 firmata digitalmente…" rows={4} data-testid="op-kb-content" />
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Titolo (opzionale)" data-testid="op-kb-title" />
          <Input value={tags} onChange={e => setTags(e.target.value)} placeholder="Tag (docfa, catasto, pregeo)" data-testid="op-kb-tags" />
          <Input type="number" min="0" max="10" value={priority} onChange={e => setPriority(Number(e.target.value) || 0)} placeholder="Priorità 0-10" data-testid="op-kb-priority" />
        </div>
        <div className="mt-3">
          <Button onClick={submit} disabled={uploading} className="ht-gradient border-0" data-testid="op-kb-save">
            {uploading ? "Salvo…" : "Salva"}
          </Button>
        </div>
      </div>
      {kb.length > 0 && (
        <div className="kpi-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-100">
                <th className="py-2 px-2">Titolo</th><th className="py-2 px-2">Kind</th><th className="py-2 px-2">Chunks</th><th className="py-2 px-2">Priorità</th><th className="py-2 px-2">Tag</th><th className="py-2 px-2 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {kb.map(k => (
                <tr key={k._id || k.id} className="border-b border-slate-50 last:border-0" data-testid={`op-kb-row-${k._id || k.id}`}>
                  <td className="py-2 px-2 font-medium truncate max-w-[300px]">{k.title}</td>
                  <td className="py-2 px-2 text-xs text-slate-500">{k.kind}</td>
                  <td className="py-2 px-2">{k.chunks_count}</td>
                  <td className="py-2 px-2">{k.priority || 0}</td>
                  <td className="py-2 px-2">
                    {(k.tags || []).length === 0 ? (
                      <span className="text-slate-400 text-xs">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {(k.tags || []).slice(0, 3).map((t, i) => (
                          <span key={`${t}-${i}`} className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{t}</span>
                        ))}
                        {k.tags.length > 3 && (
                          <span className="text-[10px] text-slate-500">+{k.tags.length - 3}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-2 text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(k)}
                      data-testid={`op-kb-edit-${k._id || k.id}`}>
                      <PencilSimple size={14} className="text-slate-700" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onDelete(k._id || k.id)}
                      data-testid={`op-kb-delete-${k._id || k.id}`}>
                      <Trash size={14} className="text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <KbEditDialog
            open={!!editing}
            item={editing}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); onChange?.(); }}
            endpointBase="/admin/chatbot-operator/knowledge"
            testidPrefix="chatbot-operator-kb"
          />
        </div>
      )}
    </div>
  );
}

function OpTemplatesTab({ templates, onChange, onDelete }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState("");
  const [uploading, setUploading] = useState(false);

  const submit = async () => {
    if (!file) { toast.error("Seleziona un file"); return; }
    if (!title) { toast.error("Titolo obbligatorio"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("title", title);
      form.append("description", description);
      form.append("category", category);
      if (tags) form.append("tags", tags);
      await api.post("/admin/chatbot-operator/template", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Template caricato");
      setFile(null); setTitle(""); setDescription(""); setCategory(""); setTags("");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="kpi-card">
        <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
          <FileText size={20} className="text-purple-700" weight="fill" /> Carica template (Word / Excel / PDF)
        </h3>
        <input type="file" onChange={e => setFile(e.target.files?.[0] || null)} className="block w-full text-sm border border-slate-200 rounded-lg p-2" data-testid="op-tpl-file" />
        {file && <div className="text-xs text-slate-500 mt-1">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Titolo *</label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Es. Modulo Docfa 4" data-testid="op-tpl-title" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Categoria</label>
            <Input value={category} onChange={e => setCategory(e.target.value)} placeholder="catasto, urbanistica, relazione_tecnica" data-testid="op-tpl-category" />
          </div>
        </div>
        <div className="mt-3">
          <label className="text-xs font-medium text-slate-600">Descrizione (indicizzata per la ricerca)</label>
          <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="Es. Modulo compilabile per la variazione catastale unità urbane…" data-testid="op-tpl-description" />
        </div>
        <div className="mt-3">
          <label className="text-xs font-medium text-slate-600">Tag</label>
          <Input value={tags} onChange={e => setTags(e.target.value)} placeholder="docfa, catasto, variazione" data-testid="op-tpl-tags" />
        </div>
        <div className="mt-4">
          <Button onClick={submit} disabled={uploading || !file} className="ht-gradient border-0" data-testid="op-tpl-save">
            {uploading ? "Caricamento…" : "Carica template"}
          </Button>
        </div>
      </div>
      {templates.length > 0 && (
        <div className="kpi-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-100">
                <th className="py-2 px-2">Titolo</th><th className="py-2 px-2">Categoria</th><th className="py-2 px-2">File</th><th className="py-2 px-2">Dim.</th><th className="py-2 px-2 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {templates.map(t => (
                <tr key={t._id || t.id} className="border-b border-slate-50 last:border-0" data-testid={`op-tpl-row-${t._id || t.id}`}>
                  <td className="py-2 px-2">
                    <div className="font-medium truncate max-w-[280px]">{t.title}</div>
                    <div className="text-[10px] text-slate-500 truncate max-w-[280px]">{t.description}</div>
                  </td>
                  <td className="py-2 px-2 text-xs">{t.category}</td>
                  <td className="py-2 px-2 text-xs text-slate-500 truncate max-w-[200px]">{t.original_filename}</td>
                  <td className="py-2 px-2 text-xs">{(t.size / 1024).toFixed(0)} KB</td>
                  <td className="py-2 px-2 text-right flex justify-end gap-1">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/operator/templates/${t._id || t.id}/download`} target="_blank" rel="noreferrer" className="p-1.5 text-purple-700 hover:bg-purple-50 rounded" title="Download">
                      <Download size={16} weight="bold" />
                    </a>
                    <Button size="sm" variant="ghost" onClick={() => onDelete(t._id || t.id)}><Trash size={14} className="text-red-600" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ============================================================
// TRAINING CHAT TAB — Chatbot Operator (KB isolata dal Pro)
// Iter209by — Gemella della TrainingChatTab del Pro, ma gli endpoint
// puntano solo alla KB `chatbot_operator_knowledge`.
// ============================================================
function OpTrainingChatTab({ onKbChange }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const bodyRef = useRef(null);

  const loadHistory = () =>
    api.get("/admin/chatbot-operator/training-messages").then(r => setMessages(r.data || []));

  useEffect(() => { loadHistory(); }, []);
  useEffect(() => { bodyRef.current?.scrollTo({ top: 999999, behavior: "smooth" }); }, [messages]);

  const send = async (bodyText, audioFilename) => {
    const text = (bodyText || "").trim();
    if (!text || sending) return;
    setSending(true);
    setMessages(prev => [...prev, { _id: `opt-${Date.now()}`, role: "admin", body: text, _local: true }]);
    setInput("");
    try {
      await api.post("/admin/chatbot-operator/training-messages", {
        body: text, audio_filename: audioFilename, save_as_knowledge: true,
      });
      await loadHistory();
      onKbChange && onKbChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setSending(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await transcribeAndSend(blob);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error("Impossibile accedere al microfono. Verifica i permessi del browser.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const transcribeAndSend = async (blob) => {
    setTranscribing(true);
    try {
      const form = new FormData();
      const fn = `voice-${Date.now()}.webm`;
      form.append("file", blob, fn);
      // Whisper endpoint condiviso (solo trascrizione audio→testo, non tocca KB)
      const r = await api.post("/admin/chatbot-tech/transcribe", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const transcribed = (r.data?.text || "").trim();
      if (!transcribed) {
        toast.error("Trascrizione vuota (audio troppo corto?)");
        return;
      }
      await send(transcribed, fn);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore trascrizione");
    } finally {
      setTranscribing(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-6">
      <div className="kpi-card flex flex-col h-[600px]">
        <div className="flex items-center gap-2 pb-3 border-b border-slate-100 mb-3">
          <div className="h-8 w-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center">
            <Robot size={16} weight="fill" />
          </div>
          <div>
            <div className="font-semibold text-sm">Chat di istruzione — Chatbot Operator</div>
            <div className="text-xs text-muted-foreground">
              Ogni tuo messaggio diventa un'istruzione nella <strong>KB Operator</strong> (isolata da quella dell'Agente AI).
            </div>
          </div>
        </div>

        <div ref={bodyRef} className="flex-1 overflow-y-auto space-y-3 pr-1" data-testid="op-training-chat-body">
          {messages.length === 0 && (
            <div className="text-center text-xs text-slate-500 mt-8 px-4">
              Inizia scrivendo una procedura o registrando un vocale. Es. <em>"Per il Docfa: verificare planimetria 1:200 firmata digitalmente prima dell'invio."</em>
            </div>
          )}
          {messages.map(m => {
            const isAdmin = m.role === "admin";
            return (
              <div key={m._id || m.id} className={`flex items-start gap-2 ${isAdmin ? "flex-row-reverse" : ""}`}>
                <div className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center ${isAdmin ? "bg-slate-200 text-slate-700" : "bg-purple-100 text-purple-700"}`}>
                  {isAdmin ? <UserIcon size={14} weight="fill" /> : <Robot size={14} weight="fill" />}
                </div>
                <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${isAdmin ? "bg-purple-700 text-white rounded-tr-sm" : "bg-white border border-slate-200 text-slate-800 rounded-tl-sm"}`}>
                  {m.body}
                  {m.audio_filename && <div className="mt-1 text-[10px] opacity-70 flex items-center gap-1"><MicrophoneStage size={10} /> {m.audio_filename}</div>}
                  {m.knowledge_id && !isAdmin && (
                    <div className="mt-1 text-[10px] opacity-70 flex items-center gap-1"><CheckCircle size={10} weight="fill" /> Salvata in KB Operator</div>
                  )}
                </div>
              </div>
            );
          })}
          {(sending || transcribing) && (
            <div className="text-xs text-slate-500 italic ml-9">
              {transcribing ? "Trascrizione audio in corso…" : "Il bot sta rispondendo…"}
            </div>
          )}
        </div>

        <div className="pt-3 border-t border-slate-100">
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
              placeholder="Scrivi un'istruzione per gli operatori…"
              rows={2}
              className="flex-1 resize-none border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              data-testid="op-training-chat-input"
            />
            {!recording ? (
              <Button
                type="button"
                variant="outline"
                onClick={startRecording}
                disabled={sending || transcribing}
                className="h-11 w-11 p-0 rounded-full"
                title="Registra audio"
                data-testid="op-training-chat-record"
              >
                <MicrophoneStage size={18} weight="fill" />
              </Button>
            ) : (
              <Button
                type="button"
                variant="destructive"
                onClick={stopRecording}
                className="h-11 w-11 p-0 rounded-full animate-pulse"
                title="Stop registrazione"
                data-testid="op-training-chat-stop"
              >
                <Stop size={18} weight="fill" />
              </Button>
            )}
            <Button
              type="button"
              onClick={() => send(input)}
              disabled={sending || !input.trim()}
              className="h-11 w-11 p-0 rounded-full ht-gradient border-0"
              title="Invia"
              data-testid="op-training-chat-send"
            >
              <PaperPlaneTilt size={18} weight="fill" />
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="kpi-card">
          <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
            <ChatText size={16} className="text-purple-700" weight="fill" /> Come istruire il bot Operator
          </h4>
          <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
            <li>Scrivi UNA procedura/regola per messaggio (es. "Prima di un Docfa 4 verificare rendita catastale in atti").</li>
            <li>Ogni messaggio è salvato solo nella KB Operator (mai condivisa con l'Agente AI).</li>
            <li>Puoi dettare con il microfono: la trascrizione avviene in italiano.</li>
            <li>Il bot ti chiede chiarimenti se un riferimento normativo/catastale è ambiguo.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
